import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Book, FileText, Target, CheckCircle } from 'lucide-react';
import ProgressService from '../services/ProgressService';

const API_BASE_URL = '/api';

const NavigationMenu = ({ currentCardId, onNavigate, isOpen, onToggle }) => {
  const [navigationData, setNavigationData] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState({});

  useEffect(() => {
    const fetchNavigation = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/cards/navigation`);
        if (!response.ok) {
          throw new Error('Failed to fetch navigation data');
        }
        const data = await response.json();
        setNavigationData(data);
        
        // Auto-expand items that contain the current card
        if (currentCardId) {
          const expandedSet = new Set();
          findAndExpandPath(data, currentCardId, expandedSet);
          setExpandedItems(expandedSet);
        }
        
        // Load progress data for completion highlighting (now synchronous)
        loadProgressData(data);
        
        setLoading(false);
      } catch (err) {
        console.error('Navigation Error:', err);
        setError('Failed to load navigation');
        setLoading(false);
      }
    };
  
    fetchNavigation();
  }, [currentCardId]);

  // Load progress data for all items
  const loadProgressData = (navData) => {
    const progress = {};
    
    const processItems = (items) => {
      for (const item of items) {
        if (item.type === 'lesson') {
          // Calculate lesson completion based on sub-lessons
          progress[item.id] = getLessonCompletion(item);
        } else if (item.type === 'subLesson') {
          // Calculate sub-lesson completion based on exercises
          progress[item.id] = getSubLessonCompletion(item);
        } else if (item.type === 'exercise') {
          // Check exercise completion from ProgressService
          progress[item.id] = {
            isCompleted: ProgressService.isExerciseCompleted(item.id),
            type: 'exercise'
          };
        }
        
        if (item.children) {
          processItems(item.children);
        }
      }
    };
    
    processItems(navData);
    setProgressData(progress);
  };

  // Calculate lesson completion based on all its sub-lessons
// Replace getLessonCompletion function:
const getLessonCompletion = (lesson) => {
  if (!lesson.children || lesson.children.length === 0) {
    return { isCompleted: false, percentage: 0, type: 'lesson' };
  }

  let totalSubLessons = 0;
  let completedSubLessons = 0;

  for (const subLesson of lesson.children) {
    if (subLesson.type === 'subLesson') {
      totalSubLessons++;
      const subLessonProgress = getSubLessonCompletion(subLesson);
      if (subLessonProgress.isCompleted) {
        completedSubLessons++;
      }
    }
  }

  const percentage = totalSubLessons > 0 ? Math.round((completedSubLessons / totalSubLessons) * 100) : 0;
  return {
    isCompleted: percentage === 100,
    percentage,
    completed: completedSubLessons,
    total: totalSubLessons,
    type: 'lesson'
  };
};

  // Calculate sub-lesson completion based on its exercises (excluding card_type="lesson")
  const getSubLessonCompletion = (subLesson) => {
    if (!subLesson.children || subLesson.children.length === 0) {
      return { isCompleted: false, percentage: 0 };
    }
  
    let totalExercises = 0;
    let completedExercises = 0;
  
    for (const exercise of subLesson.children) {
      if (exercise.type === 'exercise' && exercise.cardType === 'exercise') {
        totalExercises++;
        if (ProgressService.isExerciseCompleted(exercise.id)) {
          completedExercises++;
        }
      }
    }
  
    const percentage = totalExercises > 0 ? Math.round((completedExercises / totalExercises) * 100) : 0;
    return {
      isCompleted: percentage === 100,
      percentage
    };
  };



  // Helper function to find path to current card and expand parent items
  const findAndExpandPath = (items, targetId, expandedSet) => {
    for (const item of items) {
      if (item.id === targetId) {
        return true;
      }
      if (item.children && item.children.length > 0) {
        if (findAndExpandPath(item.children, targetId, expandedSet)) {
          expandedSet.add(item.id);
          return true;
        }
      }
    }
    return false;
  };

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleChevronClick = (e, itemId) => {
    e.stopPropagation();
    toggleExpanded(itemId);
  };

  const handleItemClick = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    
    if (item.type === 'lesson') {
      if (hasChildren) {
        toggleExpanded(item.id);
      }
    } else {
      onNavigate(item.id);
    }
  };

  const getItemIcon = (type, progress) => {
    const iconProps = { size: 16, className: "flex-shrink-0" };
    
    if (progress?.isCompleted) {
      return <CheckCircle {...iconProps} className="flex-shrink-0 text-green-600" />;
    }
    
    switch (type) {
      case 'lesson':
        return <Book {...iconProps} className="flex-shrink-0 text-blue-600" />;
      case 'subLesson':
        return <FileText {...iconProps} className="flex-shrink-0 text-green-600" />;
      case 'exercise':
        return <Target {...iconProps} className="flex-shrink-0 text-orange-600" />;
      default:
        return <FileText {...iconProps} className="flex-shrink-0" />;
    }
  };

  const getItemStyle = (item, progress) => {
    const baseStyle = "flex items-center gap-2 p-2 rounded text-left w-full transition-colors hover:bg-opacity-75";
    const isActive = currentCardId === item.id && item.type !== 'lesson';
    const isCompleted = progress?.isCompleted;
    
    if (item.type === 'lesson') {
      const completionClass = isCompleted ? 'bg-green-50 border-l-4 border-green-500' : '';
      return `${baseStyle} font-bold text-blue-800 hover:bg-blue-50 font-['bpg-caps'] text-base ${completionClass}`;
    } else if (item.type === 'subLesson') {
      const completionClass = isCompleted ? 'bg-green-50 border-l-4 border-green-500' : '';
      const activeClass = isActive ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100';
      return `${baseStyle} ml-4 ${completionClass} ${activeClass} font-['bpg-caps'] text-sm`;
    } else { // exercise
      const completionClass = isCompleted ? 'bg-green-50 border-l-4 border-green-500' : '';
      const activeClass = isActive ? 'bg-blue-100 text-blue-800' : 'text-gray-700 hover:bg-gray-50';
      return `${baseStyle} ml-8 text-sm ${completionClass} ${activeClass} font-['HelveticaRegular']`;
    }
  };

  const getProgressText = (item, progress) => {
    if (!progress) return '';
    
    if (item.type === 'lesson' && progress.total > 0) {
      return ` (${progress.completed}/${progress.total})`;
    } else if (item.type === 'subLesson' && progress.total > 0) {
      return ` (${progress.completed}/${progress.total})`;
    }
    
    return '';
  };

  const renderNavigationItem = (item) => {
    const isExpanded = expandedItems.has(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const showExpandIcon = hasChildren;
    const progress = progressData[item.id];

    return (
      <div key={item.id} className="mb-1">
        <div className="flex items-center">
          {showExpandIcon && (
            <button
              onClick={(e) => handleChevronClick(e, item.id)}
              className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? 
                <ChevronDown size={14} className="text-gray-500" /> : 
                <ChevronRight size={14} className="text-gray-500" />
              }
            </button>
          )}
          
          <button
            onClick={() => handleItemClick(item)}
            className={`${getItemStyle(item, progress)} ${showExpandIcon ? 'ml-0' : 'ml-5'} flex-1`}
          >
            <div className="flex items-center gap-2">
              {getItemIcon(item.type, progress)}
              <span className="truncate flex-1 min-w-0">
                {item.title}
                {getProgressText(item, progress)}
              </span>
            </div>
          </button>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="ml-6 border-l border-gray-200 pl-2">
            {item.children
              .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
              .map(child => renderNavigationItem(child))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) {
    return (
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={onToggle}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Open navigation menu"
        >
          <Book size={20} />
        </button>
      </div>
    );
  }

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        onClick={onToggle}
      />
      
      <div className={`
        fixed top-0 left-0 h-full bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out
        w-80 md:w-96 flex flex-col
        ${isOpen ? 'transform translate-x-0' : 'transform -translate-x-full'}
      `}>
        <div className="flex items-center justify-between p-4 border-b bg-blue-600 text-white flex-shrink-0">
          <h2 className="font-bold text-lg font-['bpg-caps']">სარჩევი</h2>
          <button
            onClick={onToggle}
            className="text-white hover:text-blue-200 transition-colors"
            aria-label="Close navigation menu"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        <div className="p-4 flex-1 overflow-y-auto">
          {loading && (
            <div className="text-center py-8">
              <div className="text-gray-500">იტვირთება...</div>
            </div>
          )}
          
          {error && (
            <div className="text-center py-8 text-red-500">
              {error}
            </div>
          )}
          
          {!loading && !error && (
            <div className="space-y-1">
              {navigationData
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map(item => renderNavigationItem(item))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NavigationMenu;